import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { fetchMyGroup } from '../../../utils/services/user.service';

const DUMMY_IMAGE = 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=400&h=240&fit=crop';

const PageContainer = styled.div`
  min-height: 100vh;
  color: white;
  padding: 2.5rem;
  position: relative;
  z-index: 0;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  position: sticky;
  top: 0;
  backdrop-filter: blur(10px);
  z-index: 1;
  padding-bottom: 1rem;
`;

const HeaderTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 600;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
`;

const BackButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: #5ce9ff;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5rem 0;
`;

const Spinner = styled.div`
  width: 3rem;
  height: 3rem;
  border: 2px solid transparent;
  border-top: 2px solid #5ce9ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const GroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const GroupCard = styled.div`
  border-radius: 1rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const CardTitle = styled.h3`
  font-weight: 600;
  font-size: 1.125rem;
  margin: 0 0 0.5rem 0;
`;

const CardDescription = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
  line-height: 1.4;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 5rem 0;

  p {
    color: #9ca3af;
    font-size: 1.125rem;
  }
`;

export default function MyGroupsList() {
  const history = useHistory();
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  const loadGroups = async () => {
    try {
      setIsLoading(true);
      const response = await fetchMyGroup({ limit, page_number: page });
      const data = response.data?.groups || response.data?.data || [];
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching my groups:', err);
      toast.error('Failed to load my groups');
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [page]);

  return (
    <PageContainer>
      <HeaderSection>
        <div>
          <HeaderTitle>My Groups</HeaderTitle>
          <HeaderSubtitle>Groups you have joined or created</HeaderSubtitle>
        </div>
        <BackButton onClick={() => history.push('/homeproducts')}>
          ← All Groups
        </BackButton>
      </HeaderSection>

      {isLoading && (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      )}

      {!isLoading && (
        <GroupGrid>
          {groups.map((group) => (
            <GroupCard key={group._id || group.id} onClick={() => history.push(`/groups/${group._id || group.id}`, { groupDetail: group })}>
              <CardImage
                src={group.image || group.coverImage || DUMMY_IMAGE}
                alt={group.group_name || group.name || 'Group'}
              />
              <CardContent>
                <CardTitle>{group.group_name || group.name || 'Unnamed Group'}</CardTitle>
                <CardDescription>
                  {group.description || 'Group purchase consortium'}
                </CardDescription>
              </CardContent>
            </GroupCard>
          ))}
        </GroupGrid>
      )}

      {!isLoading && groups.length === 0 && (
        <EmptyState>
          <p>No groups yet. Join a group from the Groups page.</p>
        </EmptyState>
      )}
    </PageContainer>
  );
}
